/**
 * Wedding Planner Template Designs
 * 8 unique visual identities — 4 free, 4 PRO
 */

export const weddingTemplates = [
  {
    id: 'ivory-edit',
    name: 'The Ivory Edit',
    tagline: 'Timeless warmth meets modern elegance',
    isPro: false,
    fonts: { heading: "'Playfair Display', serif", body: "'Lato', sans-serif" },
    colors: {
      bg: '#F5F0EB', surface: '#FFFFFF', primary: '#C9B29B',
      secondary: '#8B7355', accent: '#4A3728', text: '#3D3228', muted: '#A09080',
      border: '#E5DDD4',
    },
    coverStyle: 'editorial',
    graphicElement: 'botanical-line',
    description: 'Classic ivory tones with delicate botanical line art and warm serif typography.',
  },
  {
    id: 'scandinavian-vows',
    name: 'Scandinavian Vows',
    tagline: 'Clean lines, pure intention',
    isPro: false,
    fonts: { heading: "'DM Sans', sans-serif", body: "'Inter', sans-serif" },
    colors: {
      bg: '#FAFAF8', surface: '#FFFFFF', primary: '#8B8B8B',
      secondary: '#ABABAB', accent: '#2D2D2D', text: '#1A1A1A', muted: '#B0B0B0',
      border: '#EBEBEB',
    },
    coverStyle: 'minimal',
    graphicElement: 'geometric-thin',
    description: 'Ultra-minimalist Nordic design with generous whitespace and crisp geometry.',
  },
  {
    id: 'romantic-garden',
    name: 'Romantic Garden',
    tagline: 'Soft petals, handwritten dreams',
    isPro: false,
    fonts: { heading: "'Cormorant Garamond', serif", body: "'Nunito', sans-serif" },
    colors: {
      bg: '#FEF7F0', surface: '#FFFFFF', primary: '#D4A59A',
      secondary: '#B8D4B8', accent: '#445544', text: '#3A3A3A', muted: '#C4A8A0',
      border: '#F0E0D8',
    },
    coverStyle: 'flowing',
    graphicElement: 'watercolor-botanical',
    description: 'Soft watercolor florals with romantic serif typography and sage accents.',
  },
  {
    id: 'modern-editorial',
    name: 'Modern Editorial',
    tagline: 'Bold statements, refined taste',
    isPro: false,
    fonts: { heading: "'Montserrat', sans-serif", body: "'Source Serif 4', serif" },
    colors: {
      bg: '#FFFFFF', surface: '#F8F8F8', primary: '#111111',
      secondary: '#555555', accent: '#111111', text: '#111111', muted: '#999999',
      border: '#E0E0E0',
    },
    coverStyle: 'grid',
    graphicElement: 'bold-lines',
    description: 'High-contrast black & white with bold typography and architectural layout.',
  },
  {
    id: 'amalfi-romance',
    name: 'Amalfi Romance',
    tagline: 'Sun-kissed shores, Italian soul',
    isPro: true,
    fonts: { heading: "'Libre Baskerville', serif", body: "'Josefin Sans', sans-serif" },
    colors: {
      bg: '#FDF8F0', surface: '#FFFFFF', primary: '#C4703D',
      secondary: '#2B4C6F', accent: '#C4703D', text: '#2B2015', muted: '#B09070',
      border: '#F0E0CC',
    },
    coverStyle: 'mediterranean',
    graphicElement: 'citrus-arch',
    description: 'Warm terracotta and ocean blue with Mediterranean-inspired illustration.',
  },
  {
    id: 'black-tie',
    name: 'Black Tie',
    tagline: 'Dark luxury, golden details',
    isPro: true,
    fonts: { heading: "'Cinzel', serif", body: "'Raleway', sans-serif" },
    colors: {
      bg: '#1A1A1A', surface: '#242424', primary: '#C5A572',
      secondary: '#8A7550', accent: '#C5A572', text: '#F0EDE8', muted: '#7A7A7A',
      border: '#333333',
    },
    coverStyle: 'dark-luxury',
    graphicElement: 'art-deco',
    description: 'Dramatic dark palette with gold leaf accents and art deco geometry.',
  },
  {
    id: 'french-chateau',
    name: 'French Château',
    tagline: 'Vintage grace, Parisian charm',
    isPro: true,
    fonts: { heading: "'EB Garamond', serif", body: "'Quicksand', sans-serif" },
    colors: {
      bg: '#F7F0F5', surface: '#FFFFFF', primary: '#9B7A9B',
      secondary: '#C8A8C8', accent: '#5C4A5C', text: '#3A2E3A', muted: '#B8A0B8',
      border: '#E8D8E8',
    },
    coverStyle: 'ornate',
    graphicElement: 'french-frame',
    description: 'Lavender and dusty rose with ornate frames and French vintage elegance.',
  },
  {
    id: 'coastal-blue',
    name: 'Coastal Blue',
    tagline: 'Ocean breeze, barefoot vows',
    isPro: true,
    fonts: { heading: "'Lora', serif", body: "'Poppins', sans-serif" },
    colors: {
      bg: '#F0F5FA', surface: '#FFFFFF', primary: '#3B6B8D',
      secondary: '#D4A76A', accent: '#3B6B8D', text: '#1E3A50', muted: '#8BA8C0',
      border: '#D8E4EE',
    },
    coverStyle: 'relaxed',
    graphicElement: 'wave-pattern',
    description: 'Calming ocean palette with warm sand accents and relaxed coastal elegance.',
  },
];

export const defaultModulees = [
  { id: 'dashboard', name: 'Wedding Dashboard', icon: 'LayoutDashboard', default: true },
  { id: 'checklist', name: 'Master Checklist', icon: 'CheckSquare', default: true },
  { id: 'budget', name: 'Budget Planner', icon: 'Calculator', default: true },
  { id: 'guests', name: 'Guest List', icon: 'Users', default: true },
  { id: 'vendors', name: 'Vendors & Contacts', icon: 'Briefcase', default: true },
  { id: 'venue', name: 'Venue', icon: 'MapPin', default: true },
  { id: 'ceremony', name: 'Ceremony', icon: 'Heart', default: true },
  { id: 'reception', name: 'Reception', icon: 'Music', default: true },
  { id: 'food', name: 'Food & Drinks', icon: 'UtensilsCrossed', default: true },
  { id: 'alcohol', name: 'Alcohol Planner', icon: 'Wine', default: false },
  { id: 'seating', name: 'Seating Plan', icon: 'Grid3x3', default: true },
  { id: 'decor', name: 'Décor & Styling', icon: 'Sparkles', default: true },
  { id: 'flowers', name: 'Flowers', icon: 'Flower2', default: true },
  { id: 'photo', name: 'Photography', icon: 'Camera', default: true },
  { id: 'video', name: 'Videography', icon: 'Video', default: false },
  { id: 'beauty', name: 'Hair & Beauty', icon: 'Scissors', default: true },
  { id: 'outfits', name: 'Wedding Outfits', icon: 'Shirt', default: true },
  { id: 'transport', name: 'Transportation', icon: 'Car', default: false },
  { id: 'accommodation', name: 'Accommodation', icon: 'Hotel', default: false },
  { id: 'timeline', name: 'Wedding Day Timeline', icon: 'Clock', default: true },
  { id: 'responsibilities', name: 'Responsibilities', icon: 'ClipboardList', default: true },
  { id: 'kids', name: 'Kids Planner', icon: 'Baby', default: false },
  { id: 'emergency', name: 'Emergency Kit', icon: 'ShieldAlert', default: false },
  { id: 'honeymoon', name: 'Honeymoon', icon: 'Plane', default: false },
  { id: 'notes', name: 'Notes & Ideas', icon: 'StickyNote', default: true },
];

export function getRecommendedModulees(profile) {
  const rec = new Set(defaultModulees.filter(m => m.default).map(m => m.id));
  if (profile.childrenAttending) rec.add('kids');
  if (profile.destinationWedding) { rec.add('transport'); rec.add('accommodation'); }
  if (profile.transportNeeded) rec.add('transport');
  if (profile.accommodationNeeded) rec.add('accommodation');
  if (profile.honeymoonPlanning) rec.add('honeymoon');
  if (profile.guestCount > 60) rec.add('alcohol');
  if (profile.guestCount > 80) rec.add('video');
  if (profile.budget && parseInt(profile.budget) > 40000) { rec.add('video'); rec.add('emergency'); }
  return rec;
}
